import { useState, useEffect, useCallback, useRef } from "react";
import { workspaceAPI, analysisAPI, billingAPI } from "../../lib/api";

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

/* ── shared primitives ── */
const Label = ({ children }) => (
  <label style={{ display: "block", fontSize: 11, fontWeight: 500, color: "#9CA3AF", marginBottom: 6, letterSpacing: "0.03em", fontFamily: "'DM Sans', sans-serif" }}>
    {children}
  </label>
);

const Input = ({ label, value, onChange, placeholder, hint, type = "text" }) => (
  <div style={{ marginBottom: 18 }}>
    {label && <Label>{label}</Label>}
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: "100%", background: SURFACE_2, border: `1px solid ${BORDER}`,
        borderRadius: 8, padding: "11px 14px", fontSize: 14, color: TEXT_PRIMARY,
        outline: "none", fontFamily: "'DM Sans', sans-serif", transition: "border-color 0.2s, box-shadow 0.2s",
      }}
      onFocus={e => { e.target.style.borderColor = ACCENT; e.target.style.boxShadow = `0 0 0 3px ${ACCENT}18`; }}
      onBlur={e => { e.target.style.borderColor = BORDER; e.target.style.boxShadow = "none"; }}
    />
    {hint && <p style={{ fontSize: 11, color: TEXT_DIM, marginTop: 5, fontFamily: "'DM Sans', sans-serif" }}>{hint}</p>}
  </div>
);

const Select = ({ label, value, onChange, options }) => (
  <div style={{ marginBottom: 18 }}>
    {label && <Label>{label}</Label>}
    <select
      value={value}
      onChange={onChange}
      style={{
        width: "100%", background: SURFACE_2, border: `1px solid ${BORDER}`,
        borderRadius: 8, padding: "11px 14px", fontSize: 14, color: TEXT_PRIMARY,
        outline: "none", fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
        appearance: "none",
      }}
    >
      {options.map(o => <option key={o.value} value={o.value} style={{ background: SURFACE_2 }}>{o.label}</option>)}
    </select>
  </div>
);

const Badge = ({ label, color = ACCENT }) => (
  <span style={{
    fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace",
    color, background: `${color}18`, border: `1px solid ${color}33`,
    borderRadius: 4, padding: "2px 7px",
  }}>{label}</span>
);

const ConfBar = ({ value }) => {
  const c = value >= 80 ? "#22C55E" : value >= 60 ? "#F59E0B" : "#EF4444";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 3, background: BORDER, borderRadius: 2 }}>
        <div style={{ width: `${value}%`, height: "100%", background: c, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 11, color: c, fontFamily: "'DM Mono', monospace" }}>{value}%</span>
    </div>
  );
};

const StepIndicator = ({ steps, current }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 40 }}>
    {steps.map((s, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <div key={i} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : "none" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              background: done ? ACCENT : active ? `${ACCENT}22` : SURFACE_2,
              border: `2px solid ${done || active ? ACCENT : BORDER}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.3s",
            }}>
              {done
                ? <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.5}><polyline points="20 6 9 17 4 12" /></svg>
                : <span style={{ fontSize: 11, fontWeight: 700, color: active ? ACCENT : TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{i + 1}</span>
              }
            </div>
            <span style={{ fontSize: 10, color: active ? ACCENT : done ? TEXT_MUTED : TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
              {s}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div style={{ flex: 1, height: 1, background: done ? ACCENT : BORDER, margin: "0 8px", marginBottom: 20, transition: "background 0.3s" }} />
          )}
        </div>
      );
    })}
  </div>
);

/* ══ STEP 1: INPUT ══ */
const StepInput = ({ onNext }) => {
  const [form, setForm] = useState({ name: "", domain: "", country: "", persona: "", tier: "" });
  const [errors, setErrors] = useState({});
  const set = k => e => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
  };

  const handleSubmit = () => {
    const next = {};
    if (!form.name.trim())    next.name    = "Company name is required";
    if (!form.domain.trim())  next.domain  = "Domain is required";
    if (!form.country)        next.country = "Country / Region is required";
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    setErrors({});
    onNext(form);
  };

  const errStyle = { fontSize: 11.5, color: "#F87171", fontWeight: 500, marginTop: -10, marginBottom: 14, fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 6 };
  const ErrIcon = () => (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx={12} cy={12} r={10} /><line x1={12} y1={8} x2={12} y2={12} /><line x1={12} y1={16} x2={12.01} y2={16} />
    </svg>
  );

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", marginBottom: 6, fontFamily: "'DM Sans', sans-serif" }}>
          New Analysis
        </h2>
        <p style={{ fontSize: 13, color: TEXT_MUTED }}>
          Enter company details. Inference will resolve identity, then scan and generate structured outputs.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
        <div>
          <Input label="Company Name *" value={form.name} onChange={set("name")} placeholder="e.g. Shopify" />
          {errors.name && <div data-testid="err-name" style={errStyle}><ErrIcon />{errors.name}</div>}
        </div>
        <div>
          <Input label="Domain *" value={form.domain} onChange={set("domain")} placeholder="e.g. shopify.com" />
          {errors.domain && <div data-testid="err-domain" style={errStyle}><ErrIcon />{errors.domain}</div>}
        </div>
      </div>

      <Select
        label="Country / Region *"
        value={form.country}
        onChange={set("country")}
        options={[
          { value: "", label: "Select country…" },
          { value: "us", label: "United States" },
          { value: "ca", label: "Canada" },
          { value: "gb", label: "United Kingdom" },
          { value: "de", label: "Germany" },
          { value: "fr", label: "France" },
          { value: "au", label: "Australia" },
          { value: "sg", label: "Singapore" },
          { value: "other", label: "Other" },
        ]}
      />
      {errors.country && <div data-testid="err-country" style={errStyle}><ErrIcon />{errors.country}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
        <Select
          label="Target Persona"
          value={form.persona}
          onChange={set("persona")}
          options={[
            { value: "", label: "Select persona…" },
            { value: "payments", label: "VP / Head of Payments" },
            { value: "engineering", label: "Engineering / CTO" },
            { value: "finance", label: "CFO / Finance" },
            { value: "product", label: "CPO / Product" },
            { value: "bd", label: "BD / Partnerships" },
            { value: "exec", label: "CEO / Executive" },
          ]}
        />
        <Select
          label="Priority Tier"
          value={form.tier}
          onChange={set("tier")}
          options={[
            { value: "", label: "No tier set" },
            { value: "1", label: "Tier 1 — High priority" },
            { value: "2", label: "Tier 2 — Medium priority" },
            { value: "3", label: "Tier 3 — Watch list" },
          ]}
        />
      </div>

      <button
        data-testid="begin-scan-btn"
        onClick={handleSubmit}
        style={{
          width: "100%", background: ACCENT,
          color: "#fff", border: "none", borderRadius: 9,
          padding: "13px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer",
          fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.01em",
          transition: "background 0.2s",
        }}
      >
        Resolve Identity + Begin Scan →
      </button>
      <p style={{ fontSize: 11, color: TEXT_DIM, textAlign: "center", marginTop: 10, fontFamily: "'DM Sans', sans-serif" }}>
        Inference will confirm identity before scanning begins
      </p>
    </div>
  );
};

/* ══ STEP 2: IDENTITY CONFIRMATION ══ */
const StepIdentity = ({ form, onConfirm, onEdit, submitting = false }) => {
  const [selected, setSelected] = useState(0);

  // Identity resolution now shows ONLY the user's typed input as the single
  // target. Previously this step had a hardcoded 3-row array of Shopify
  // variants (Shopify Inc. / Shopify Plus / Shopify Payments) with fake
  // Canadian-Business-Registry confidence scores — which displayed on every
  // analysis regardless of what the user typed. That was misleading demo
  // data leaking into production. A future improvement would call the real
  // entity-resolution API to surface actual variants; until then, only the
  // user's own input is presented.
  const candidates = [
    {
      name: form.name || "",
      domain: form.domain || "",
      country: form.country || "",
      registry: "As entered",
      confidence: 100,
      type: "Target company",
      ticker: null,
    },
  ];

  const sel = candidates[selected];
  const confColor = v => v >= 80 ? "#22C55E" : v >= 60 ? "#F59E0B" : "#EF4444";

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", marginBottom: 6, fontFamily: "'DM Sans', sans-serif" }}>
          Identity Confirmation
        </h2>
        <p style={{ fontSize: 13, color: TEXT_MUTED }}>
          Confirm the entity to analyse. Click Edit if the domain or country needs adjusting.
          <span style={{ marginLeft: 8, fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", opacity: 0.6 }}>[v53]</span>
        </p>
      </div>

      {/* Candidates */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
        {candidates.map((c, i) => (
          <button
            key={i}
            onClick={() => setSelected(i)}
            style={{
              background: selected === i ? `${ACCENT}0F` : SURFACE,
              border: `1px solid ${selected === i ? ACCENT + "55" : BORDER}`,
              borderRadius: 10, padding: "16px 18px", cursor: "pointer",
              textAlign: "left", transition: "all 0.15s",
              display: "flex", alignItems: "center", gap: 14,
            }}
          >
            <div style={{
              width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
              background: selected === i ? ACCENT : SURFACE_2,
              border: `2px solid ${selected === i ? ACCENT : BORDER}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {selected === i && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>{c.name}</span>
                <Badge label={c.type} color={selected === i ? ACCENT : TEXT_DIM} />
                {c.ticker && <Badge label={c.ticker} color="#22C55E" />}
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
                {[c.domain, c.country, c.registry].map((v, j) => (
                  <span key={j} style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{v}</span>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>IDENTITY CONFIDENCE</span>
                <div style={{ flex: 1, maxWidth: 140, height: 3, background: BORDER, borderRadius: 2 }}>
                  <div style={{ width: `${c.confidence}%`, height: "100%", background: confColor(c.confidence), borderRadius: 2 }} />
                </div>
                <span style={{ fontSize: 11, color: confColor(c.confidence), fontFamily: "'DM Mono', monospace" }}>{c.confidence}%</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Mismatch warnings */}
      {sel.confidence < 80 && (
        <div style={{
          background: "#1C1200", border: "1px solid #78350F", borderRadius: 9,
          padding: "12px 16px", marginBottom: 20, display: "flex", gap: 10,
        }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#F59E0B", marginBottom: 3 }}>Low confidence match</div>
            <div style={{ fontSize: 11, color: TEXT_MUTED }}>This candidate has a confidence below 80%. Scan results may be incomplete or attributed to the wrong entity. Consider editing input or proceeding with caution.</div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onEdit} disabled={submitting} style={{
          flex: 1, background: SURFACE_2, border: `1px solid ${BORDER}`,
          borderRadius: 9, padding: "12px", fontSize: 13, fontWeight: 500,
          color: TEXT_MUTED, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "'DM Sans', sans-serif",
          opacity: submitting ? 0.5 : 1,
          transition: "all 0.15s",
        }}>← Edit Input</button>
        <button
          data-testid="confirm-begin-scan-btn"
          onClick={() => onConfirm(sel)}
          disabled={submitting}
          style={{
            flex: 2, background: ACCENT, color: "#fff", border: "none",
            borderRadius: 9, padding: "12px", fontSize: 14, fontWeight: 600,
            cursor: submitting ? "not-allowed" : "pointer", fontFamily: "'DM Sans', sans-serif",
            opacity: submitting ? 0.65 : 1,
            transition: "background 0.2s, opacity 0.2s",
          }}
        >
          {submitting ? "Scanning…" : "Confirm Identity + Begin Scan →"}
        </button>
      </div>
    </div>
  );
};

/* ══ STEP 3: PROCESSING ══ */
const StepProcessing = ({ company, workspaceId, analysisId, onComplete }) => {
  const JOBS = [
    { id: "identity", label: "Identity resolution", duration: 600 },
    { id: "fetch", label: "Fetching pages (homepage, pricing, checkout)", duration: 1200 },
    { id: "payments", label: "Payments + BNPL detection", duration: 900 },
    { id: "signals", label: "Signal extraction", duration: 1100 },
    { id: "classification", label: "Classification + hypothesis generation", duration: 1400 },
    { id: "meddicc", label: "MEDDICC mapping", duration: 1000 },
    { id: "contacts", label: "Decision-maker contact mapping", duration: 800 },
    { id: "articles", label: "Article discovery + relevance scoring", duration: 700 },
    { id: "fit", label: "Fit score calculation", duration: 500 },
    { id: "brief", label: "Generating exportable brief", duration: 600 },
  ];

  const [jobStates, setJobStates] = useState(JOBS.reduce((acc, j) => ({ ...acc, [j.id]: "queued" }), {}));
  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);
  const counterRef = useRef(null);

  // Freeze the elapsed timer at the moment the analysis is done. Previously
  // the setInterval kept ticking after all modules turned green, so the
  // "45s elapsed" label would keep climbing on the completion screen —
  // confusing users because the number no longer reflected real work.
  useEffect(() => {
    if (done && counterRef.current) {
      clearInterval(counterRef.current);
      counterRef.current = null;
    }
  }, [done]);

  useEffect(() => {
    // Visual animation for the job progress.
    //
    // We deliberately hold the LAST module in the "running" state until the
    // real backend poll returns completed. Previously the visual timers
    // marked all 10 modules "done" in about 9 seconds regardless of the
    // real backend state — so the user would sit on a "100% DONE" screen
    // for another 30-60 seconds waiting for the analysis to actually
    // finish, with no visual feedback that work was still ongoing. Now the
    // visual can only reach 9/10 modules until the poll confirms real
    // completion.
    let t = 0;
    const timers = [];
    JOBS.forEach((job, idx) => {
      const start = t + 200;
      const end = start + job.duration;
      timers.push(setTimeout(() => setJobStates(s => ({ ...s, [job.id]: "running" })), start));
      // Only flip to "done" for the first N-1 modules; the last one stays
      // "running" until the real poll below marks it done.
      if (idx < JOBS.length - 1) {
        timers.push(setTimeout(() => setJobStates(s => ({ ...s, [job.id]: "done" })), end));
      }
      t = end + 100;
    });

    // The elapsed counter runs while polling is active. It is stopped in a
    // separate effect above the moment `done` flips to true (see useEffect
    // on [done]). We stash the interval id in a ref so the sibling effect
    // can reach it.
    counterRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    const counter = counterRef.current;

    // Poll real API for completion
    let pollTimer;
    if (workspaceId && analysisId) {
      const pollInterval = 3000;
      const maxAttempts = 60;
      let attempts = 0;
      const poll = async () => {
        try {
          const res = await analysisAPI.poll(workspaceId, analysisId);
          if (res.data.status === "completed" || res.data.status === "failed") {
            // Force all jobs to done state
            setJobStates(JOBS.reduce((acc, j) => ({ ...acc, [j.id]: "done" }), {}));
            setDone(true);
            return;
          }
          attempts++;
          if (attempts < maxAttempts) {
            pollTimer = setTimeout(poll, pollInterval);
          } else {
            setDone(true);
          }
        } catch {
          attempts++;
          if (attempts < maxAttempts) pollTimer = setTimeout(poll, pollInterval);
        }
      };
      pollTimer = setTimeout(poll, 2000);
    } else {
      // No real API - use animation timers to complete
      timers.push(setTimeout(() => setDone(true), t + 400));
    }

    return () => { timers.forEach(clearTimeout); clearInterval(counter); if (pollTimer) clearTimeout(pollTimer); };
  }, [workspaceId, analysisId]);

  const completed = Object.values(jobStates).filter(s => s === "done").length;
  const progress = Math.round((completed / JOBS.length) * 100);

  return (
    <div style={{ maxWidth: 600, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 36 }}>
        {done ? (
          <>
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: "#052E16", border: "1px solid #166534",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px",
            }}>
              <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth={2}><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.02em", marginBottom: 6, fontFamily: "'DM Sans', sans-serif" }}>
              Analysis complete
            </h2>
            <p style={{ fontSize: 13, color: TEXT_MUTED }}>All {JOBS.length} modules completed in {elapsed}s. Results are ready.</p>
          </>
        ) : (
          <>
            <div style={{ position: "relative", width: 56, height: 56, margin: "0 auto 16px" }}>
              <svg width={56} height={56} style={{ transform: "rotate(-90deg)" }}>
                <circle cx={28} cy={28} r={24} fill="none" stroke={BORDER} strokeWidth={3} />
                <circle cx={28} cy={28} r={24} fill="none" stroke={ACCENT} strokeWidth={3}
                  strokeDasharray={`${2 * Math.PI * 24}`}
                  strokeDashoffset={`${2 * Math.PI * 24 * (1 - progress / 100)}`}
                  style={{ transition: "stroke-dashoffset 0.4s" }}
                />
              </svg>
              <span style={{
                position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700, color: ACCENT, fontFamily: "'DM Mono', monospace",
              }}>{progress}%</span>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.02em", marginBottom: 6, fontFamily: "'DM Sans', sans-serif" }}>
              Analysing {company?.name || "company"}…
            </h2>
            <p style={{ fontSize: 13, color: TEXT_MUTED }}>Running {JOBS.length} intelligence modules · {elapsed}s elapsed</p>
          </>
        )}
      </div>

      {/* Progress bar */}
      <div style={{ height: 3, background: BORDER, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ width: `${progress}%`, height: "100%", background: ACCENT, borderRadius: 2, transition: "width 0.4s" }} />
      </div>

      {/* Job list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 28 }}>
        {JOBS.map(job => {
          const state = jobStates[job.id];
          return (
            <div key={job.id} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "9px 14px", borderRadius: 8,
              background: state === "running" ? `${ACCENT}0A` : state === "done" ? "#0A150A" : SURFACE,
              border: `1px solid ${state === "running" ? ACCENT + "33" : state === "done" ? "#166534" : BORDER}`,
              transition: "all 0.3s",
            }}>
              <div style={{ width: 18, height: 18, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {state === "done" && (
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth={2.5}><polyline points="20 6 9 17 4 12" /></svg>
                )}
                {state === "running" && (
                  <div style={{ width: 14, height: 14, border: `2px solid ${ACCENT}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                )}
                {state === "queued" && (
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: TEXT_DIM }} />
                )}
              </div>
              <span style={{
                fontSize: 13, fontFamily: "'DM Sans', sans-serif",
                color: state === "done" ? "#22C55E" : state === "running" ? TEXT_PRIMARY : TEXT_DIM,
                fontWeight: state === "running" ? 500 : 400,
              }}>{job.label}</span>
              {state === "running" && (
                <span style={{ marginLeft: "auto", fontSize: 10, color: ACCENT, fontFamily: "'DM Mono', monospace" }}>RUNNING</span>
              )}
              {state === "done" && (
                <span style={{ marginLeft: "auto", fontSize: 10, color: "#22C55E", fontFamily: "'DM Mono', monospace" }}>DONE</span>
              )}
            </div>
          );
        })}
      </div>

      {done && (
        <button onClick={onComplete} style={{
          width: "100%", background: ACCENT, color: "#fff", border: "none",
          borderRadius: 9, padding: "14px", fontSize: 15, fontWeight: 600,
          cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          animation: "fadeIn 0.4s ease",
        }}>
          View Results →
        </button>
      )}
    </div>
  );
};

/* ══ ROOT ══ */
export default function InferenceNewAnalysis({ workspaceId, onAnalysisComplete }) {
  const [step, setStep] = useState(0); // 0=input, 1=identity, 2=processing, 3=done
  const [formData, setFormData] = useState(null);
  const [confirmedEntity, setConfirmedEntity] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [capInfo, setCapInfo] = useState(null);
  const [showCapModal, setShowCapModal] = useState(false);
  const [overageQty, setOverageQty] = useState(10);
  // Submission guard: prevents the user from triggering multiple analyses
  // by double-clicking the Confirm button before the screen transitions to
  // the PROCESSING step. Each duplicate click historically created an extra
  // db.analyses row + an extra full pipeline run (8-10 Exa calls each).
  const [submitting, setSubmitting] = useState(false);

  const STEPS = ["INPUT", "IDENTITY", "PROCESSING", "RESULTS"];

  const handleFormSubmit = async (data) => {
    setFormData(data);
    setStep(1);
  };

  const handleConfirm = async (entity) => {
    if (submitting) return;
    setSubmitting(true);
    setConfirmedEntity(entity);
    if (!workspaceId) { setSubmitting(false); setStep(2); return; }

    // Part 10 — Check cap before running analysis
    try {
      const capRes = await billingAPI.usage();
      const cap = capRes.data;
      if (cap && !cap.allowed) {
        setCapInfo(cap);
        setShowCapModal(true);
        setSubmitting(false);
        return;
      }
      // Show 90% warning banner (non-blocking)
      if (cap && cap.at_90_percent) {
        setCapInfo(cap);
      }
    } catch (e) {
      // Cap check failed — allow analysis to proceed
    }

    try {
      const companyInput = `${entity.name || formData?.name} (${entity.domain || formData?.domain})`;
      const res = await analysisAPI.create(workspaceId, companyInput);
      const aid = res.data.analysis_id;
      setAnalysisId(aid);
      await analysisAPI.process(workspaceId, aid);
      setStep(2);
      // On success the user is now on the PROCESSING step — no need to
      // reset `submitting`, the component re-renders without the Confirm
      // button visible.
    } catch (err) {
      // Check if it's a cap_reached error from backend
      if (err?.response?.status === 403 && err?.response?.data?.detail === "cap_reached") {
        try {
          const capRes = await billingAPI.usage();
          setCapInfo(capRes.data);
          setShowCapModal(true);
        } catch (e) {
          setShowCapModal(true);
          setCapInfo({ used: 0, cap: 0, remaining: 0, overage_rate: 0, next_reset: "" });
        }
        setSubmitting(false);
        return;
      }
      console.error('Failed to start analysis:', err);
      setSubmitting(false);
      setStep(2);
    }
  };

  const handleProcessingComplete = useCallback(async () => {
    if (workspaceId && analysisId) {
      try {
        const res = await analysisAPI.get(workspaceId, analysisId);
        setAnalysisResult(res.data);
      } catch (err) {
        console.error('Failed to fetch analysis result:', err);
      }
    }
    setStep(3);
    // Belt-and-suspenders reset: when the analysis finishes processing
    // and the user lands on the RESULTS screen, ensure the submission
    // guard is cleared so any subsequent "Analyse Another" flow starts
    // from a clean slate.
    setSubmitting(false);
  }, [workspaceId, analysisId]);

  const handleViewResults = () => {
    if (onAnalysisComplete && analysisResult) {
      onAnalysisComplete(analysisResult);
    }
  };

  return (
    <div data-testid="new-analysis-page" style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        select option { background: #0D1117; }
        select { color: #F9FAFB; }
      `}</style>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "48px 28px" }}>
        <StepIndicator steps={STEPS} current={step} />

        <div style={{ animation: "fadeIn 0.35s ease" }} key={step}>
          {step === 0 && (
            <StepInput onNext={handleFormSubmit} />
          )}
          {step === 1 && (
            <StepIdentity
              form={formData}
              onConfirm={handleConfirm}
              onEdit={() => setStep(0)}
              submitting={submitting}
            />
          )}
          {step === 2 && (
            <StepProcessing
              company={confirmedEntity}
              workspaceId={workspaceId}
              analysisId={analysisId}
              onComplete={handleProcessingComplete}
            />
          )}
          {step === 3 && (
            <div style={{ textAlign: "center", maxWidth: 500, margin: "0 auto" }}>
              <div style={{
                width: 64, height: 64, borderRadius: "50%", background: `${ACCENT}18`, border: `1px solid ${ACCENT}44`,
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px",
              }}>
                <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth={1.8}><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}>
                Results ready
              </h2>
              <p style={{ fontSize: 14, color: TEXT_MUTED, marginBottom: 28, lineHeight: 1.65 }}>
                Analysis for <strong style={{ color: TEXT_PRIMARY }}>{confirmedEntity?.name || formData?.name}</strong> is complete.
                All modules ran successfully.{analysisResult?.fit_score && <> Fit score: <strong style={{ color: "#22C55E" }}>{analysisResult.fit_score}</strong></>}
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                <button data-testid="analyze-another-btn" onClick={() => { setStep(0); setAnalysisId(null); setAnalysisResult(null); setSubmitting(false); }} style={{
                  background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 9,
                  padding: "12px 22px", fontSize: 13, color: TEXT_MUTED, cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}>Analyse Another</button>
                <button data-testid="open-results-btn" onClick={handleViewResults} style={{
                  background: ACCENT, color: "#fff", border: "none", borderRadius: 9,
                  padding: "12px 28px", fontSize: 14, fontWeight: 600, cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}>Open Results →</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Part 10.2 — 90% Cap Warning Banner */}
      {capInfo && capInfo.at_90_percent && !showCapModal && (() => {
        const isTrial = typeof capInfo.phase === "string" && capInfo.phase.startsWith("trial_phase_");
        const period = isTrial ? "in your free trial" : "this month";
        return (
        <div data-testid="cap-warning-banner" style={{
          position: "fixed", bottom: 0, left: 0, right: 0, padding: "10px 20px",
          background: "#F59E0B22", borderTop: "1px solid #F59E0B44", display: "flex",
          alignItems: "center", justifyContent: "center", gap: 12, zIndex: 100,
        }}>
          <span style={{ fontSize: 12, color: "#F59E0B" }}>
            You have used {capInfo.used} of {capInfo.cap} analyses {period}. Only {capInfo.remaining} remaining — consider upgrading or purchasing more.
          </span>
          <button onClick={() => setCapInfo(null)} style={{ background: "none", border: "none", color: "#F59E0B", cursor: "pointer", fontSize: 14 }}>x</button>
        </div>
        );
      })()}

      {/* Part 10.3 — Cap Reached Modal */}
      {showCapModal && (() => {
        const isTrial = typeof capInfo?.phase === "string" && capInfo.phase.startsWith("trial_phase_");
        const period = isTrial ? "in your free trial so far" : "this month";
        const heading = isTrial ? "Free Trial Limit Reached" : "Monthly Analysis Cap Reached";
        return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div data-testid="cap-reached-modal" role="dialog" style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "32px", width: 480, maxHeight: "80vh", overflowY: "auto" }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 6 }}>{heading}</h2>
            <p style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 24 }}>
              You&apos;ve used all {capInfo?.cap || 0} analyses {period}. Choose an option to continue.
            </p>
            {capInfo?.overage_rate > 0 && (
              <div data-testid="overage-option" style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 18px", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 8 }}>Buy more analyses</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: TEXT_MUTED }}>Qty:</span>
                  <input data-testid="overage-quantity" type="number" value={overageQty} onChange={e => setOverageQty(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
                    style={{ width: 60, background: BG, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "4px 8px", color: TEXT_PRIMARY, fontSize: 13, textAlign: "center" }} />
                  <span style={{ fontSize: 11, color: TEXT_DIM }}>@ ${capInfo.overage_rate}/ea</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: ACCENT, marginLeft: "auto" }}>${(capInfo.overage_rate * overageQty).toFixed(2)}</span>
                </div>
                <button data-testid="purchase-overage-btn" onClick={async () => {
                  try { const res = await billingAPI.purchaseOverage(overageQty, window.location.origin); if (res.data?.checkout_url) window.location.href = res.data.checkout_url; } catch (e) { console.error(e); }
                }} style={{ width: "100%", padding: "10px 0", borderRadius: 8, background: ACCENT, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Purchase {overageQty} analyses for ${(capInfo.overage_rate * overageQty).toFixed(2)}
                </button>
              </div>
            )}
            <div data-testid="upgrade-option" style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 18px", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>Upgrade your plan</div>
              <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 10 }}>Get more analyses and unlock premium features.</div>
              <button data-testid="upgrade-tier-btn" style={{ width: "100%", padding: "10px 0", borderRadius: 8, background: SURFACE, color: ACCENT, border: `1px solid ${ACCENT}`, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>View Plans</button>
            </div>
            <div data-testid="wait-option" style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 18px" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>Wait for monthly reset</div>
              <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 10 }}>Resets {capInfo?.next_reset ? new Date(capInfo.next_reset).toLocaleDateString() : "1st of next month"}.</div>
              <button data-testid="close-cap-modal" onClick={() => setShowCapModal(false)}
                style={{ width: "100%", padding: "10px 0", borderRadius: 8, background: SURFACE, color: TEXT_MUTED, border: `1px solid ${BORDER}`, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Close</button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}